import React from 'react';
import api from '../services/api';

// commons #224 — one payment-methods panel for BOTH the patient panel and Care
// Hub → appointment → Payments. Shows the transient intake card vault
// ("From intake — for Athena entry": reveal 60 s, copy, Entered in Athena),
// athena stored cards, and Stripe methods. Extracted from PatientDetailModal
// after the tester looked for the vaulted card on the Care Hub tab, which only
// listed Stripe methods.
export default function PaymentMethodsPanel({ patientId, practiceId }) {
  const [stripe, setStripe] = React.useState([]);
  const [stored, setStored] = React.useState([]);
  const [vault, setVault] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [deletingId, setDeletingId] = React.useState(null);
  // commons #224: revealed card data lives only in component state, for a
  // short window, and is never written anywhere else.
  const [revealed, setRevealed] = React.useState(null);   // { id, number, cvv, ... }
  const [revealLeft, setRevealLeft] = React.useState(0);
  const [vaultBusy, setVaultBusy] = React.useState(null);
  const [denied, setDenied] = React.useState(false);
  const [loadError, setLoadError] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setDenied(false);
    setLoadError(null);
    // Swallowing every failure into an empty array meant a 403 rendered as
    // "No saved payment methods." The stored-card endpoints require billing:view,
    // which is admins-only, while this panel is reachable by anyone with
    // appointments:view — so a nurse was told the patient had no card while a
    // vaulted intake card sat there waiting to be keyed into athena, which is the
    // whole reason the vault exists. Distinguish "none" from "not allowed to see".
    const attempt = async (p) => {
      try { return { ok: true, res: await p }; }
      catch (err) { return { ok: false, status: err?.status, err }; }
    };
    try {
      const [s, c, v] = await Promise.all([
        attempt(api.request(`/patients/${patientId}/payment-methods`)),
        practiceId
          ? attempt(api.request(`/practices/${practiceId}/patients/${patientId}/stored-card`))
          : Promise.resolve({ ok: true, res: { data: [] } }),
        practiceId
          ? attempt(api.request(`/practices/${practiceId}/patients/${patientId}/stored-card/vault`))
          : Promise.resolve({ ok: true, res: { data: [] } })
      ]);
      const results = [s, c, v];
      if (results.some(r => !r.ok && (r.status === 403 || r.status === 401))) setDenied(true);
      else if (results.some(r => !r.ok)) setLoadError(results.find(r => !r.ok)?.err?.message || 'Could not load payment methods.');
      setStripe(s.ok ? (s.res.data || []) : []);
      setStored(c.ok ? (c.res.data || []).filter(m => m.source !== 'intake') : []);
      setVault(v.ok ? (v.res.data || []) : []);
    } finally {
      setLoading(false);
    }
  }, [patientId, practiceId]);

  // Auto re-mask after 60 s.
  React.useEffect(() => {
    if (!revealed) return undefined;
    setRevealLeft(60);
    const t = setInterval(() => setRevealLeft(n => {
      if (n <= 1) { clearInterval(t); setRevealed(null); return 0; }
      return n - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [revealed]);

  const handleReveal = async (card) => {
    setVaultBusy(card.id);
    try {
      const r = await api.request(`/practices/${practiceId}/patients/${patientId}/stored-card/${card.id}/reveal`, { method: 'POST' });
      setRevealed(r.data);
    } catch (err) {
      alert('Reveal failed: ' + (err.body?.error || err.message));
    } finally {
      setVaultBusy(null);
    }
  };

  const handleEnteredInEhr = async (card) => {
    if (!window.confirm(`Confirm the card •••• ${card.card_last_four} has been entered in Athena? The number and CVV will be permanently deleted from Newton.`)) return;
    setVaultBusy(card.id);
    try {
      await api.request(`/practices/${practiceId}/patients/${patientId}/stored-card/${card.id}/entered-in-ehr`, { method: 'POST' });
      setRevealed(null);
      await load();
    } catch (err) {
      alert('Failed: ' + (err.body?.error || err.message));
    } finally {
      setVaultBusy(null);
    }
  };

  const copy = (label, value) => {
    if (!value) return;
    navigator.clipboard?.writeText(String(value)).then(() => {}, () => alert(`${label}: ${value}`));
  };

  React.useEffect(() => { load(); }, [load]);

  const handleDelete = async (card) => {
    if (!window.confirm(`Remove ${(card.card_brand || 'card').toUpperCase()} •••• ${card.card_last_four}? This deletes the card from athena too.`)) return;
    setDeletingId(card.id);
    try {
      await api.request(`/practices/${practiceId}/patients/${patientId}/stored-card/${card.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      if (err.body?.code === 'ATHENA_SCOPE_NOT_APPROVED') {
        alert('Deleting stored cards requires Athena Go-Live approval for this practice.');
      } else {
        alert('Delete failed: ' + (err.body?.error || err.message));
      }
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <p style={{ color: '#9ca3af', textAlign: 'center', padding: 24 }}>Loading payment methods...</p>;
  if (denied) return <p style={{ color: '#92400e', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 6, textAlign: 'center', padding: 16, margin: 0 }}>You do not have permission to view saved cards for this patient. A practice admin can see them. This is not the same as the patient having no card on file.</p>;
  if (loadError) return <p style={{ color: '#991b1b', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, textAlign: 'center', padding: 16, margin: 0 }}>Could not load payment methods: {loadError}. Try again before assuming there is no card on file.</p>;
  if (stripe.length === 0 && stored.length === 0 && vault.length === 0) return <p style={{ color: '#9ca3af', textAlign: 'center', padding: 24 }}>No saved payment methods. Cards are saved when a payment is collected.</p>;

  const fmtExp = (m, y) => (m && y ? `${String(m).padStart(2, '0')}/${String(y).slice(-2)}` : '—');

  return (
    <div>
      {vault.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, padding: '8px 0 4px' }}>
            From intake — for Athena entry
          </div>
          {vault.map(m => {
            const isOpen = revealed && revealed.id === m.id;
            return (
              <div key={`v-${m.id}`} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 20 }}>{m.revealable ? '🔐' : '🗑️'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {(m.card_brand || 'card').toUpperCase()} •••• {m.card_last_four}
                      <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12, marginLeft: 8 }}>exp {fmtExp(m.card_exp_month, m.card_exp_year)}{m.name_on_card ? ` · ${m.name_on_card}` : ''}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                      {m.appointment_date ? `Appt ${String(m.appointment_date).slice(0, 10)}` : 'No appointment'}
                      {m.purged_at
                        ? ` · purged (${m.purge_reason || 'done'})${m.entered_in_ehr_at ? ' — entered in Athena' : ''}`
                        : m.revealable ? ` · number & CVV held until checkout${m.reveal_count ? ` · revealed ${m.reveal_count}×` : ''}` : ''}
                    </div>
                  </div>
                  {m.revealable && !isOpen && (
                    <button onClick={() => handleReveal(m)} disabled={vaultBusy === m.id}
                      style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#1d4ed8', background: '#fff', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer' }}>
                      {vaultBusy === m.id ? 'Revealing…' : 'Reveal'}
                    </button>
                  )}
                  {m.revealable && (
                    <button onClick={() => handleEnteredInEhr(m)} disabled={vaultBusy === m.id}
                      style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#065f46', background: '#fff', border: '1px solid #a7f3d0', borderRadius: 6, cursor: 'pointer', marginLeft: 8 }}>
                      Entered in Athena
                    </button>
                  )}
                </div>
                {isOpen && (
                  <div style={{ marginTop: 10, padding: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontFamily: 'inherit' }}>
                      <span style={{ fontSize: 11, color: '#92400e', fontFamily: 'system-ui' }}>Visible for {revealLeft}s — type or copy into Athena, then click "Entered in Athena"</span>
                      <button onClick={() => setRevealed(null)} style={{ fontSize: 11, border: 'none', background: 'none', color: '#92400e', cursor: 'pointer' }}>hide</button>
                    </div>
                    {[['Number', revealed.number], ['Expiry', fmtExp(revealed.exp_month, revealed.exp_year)], ['CVV', revealed.cvv || '(not provided)'], ['Name', revealed.name_on_card || '—'], ['ZIP', revealed.billing_zip || '—']].map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '3px 0' }}>
                        <span style={{ width: 64, color: '#6b7280', fontFamily: 'system-ui', fontSize: 12 }}>{label}</span>
                        <span style={{ flex: 1, letterSpacing: label === 'Number' ? 1 : 0 }}>{value}</span>
                        <button onClick={() => copy(label, value)} style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', cursor: 'pointer', fontFamily: 'system-ui' }}>copy</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
      {stored.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, padding: '8px 0 4px' }}>
            On File (athena)
          </div>
          {stored.map(m => (
            <div key={`s-${m.id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: 20 }}>💳</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{(m.card_brand || '').toUpperCase()} •••• {m.card_last_four}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>
                  {m.card_exp_month && m.card_exp_year ? `Expires ${String(m.card_exp_month).padStart(2, '0')}/${String(m.card_exp_year).slice(-2)}` : 'Stored on athena'}
                </div>
              </div>
              <button
                onClick={() => handleDelete(m)}
                disabled={deletingId === m.id}
                style={{
                  padding: '5px 12px', fontSize: 12, fontWeight: 600,
                  color: '#b91c1c', background: '#fff', border: '1px solid #fecaca',
                  borderRadius: 6, cursor: deletingId === m.id ? 'not-allowed' : 'pointer'
                }}
              >
                {deletingId === m.id ? 'Removing…' : 'Remove'}
              </button>
            </div>
          ))}
        </>
      )}
      {stripe.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, padding: '16px 0 4px' }}>
            Stripe
          </div>
          {stripe.map(m => (
            <div key={`stripe-${m.id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ fontSize: 20 }}>💳</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{(m.brand || '').toUpperCase()} •••• {m.last4}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>Expires {m.expMonth}/{m.expYear}</div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
